/**
 * GraphQL Notification Mutation Hooks
 *
 * Hooks for notification operations: mark read, mark all read.
 */

import { useMutation, gql } from '@apollo/client';
import logger from '../../utils/logger';

// GraphQL Mutations
const MARK_NOTIFICATION_READ_MUTATION = gql`
  mutation MarkNotificationRead($input: MarkNotificationReadInput!) {
    markNotificationRead(input: $input)
  }
`;

const MARK_ALL_NOTIFICATIONS_READ_MUTATION = gql`
  mutation MarkAllNotificationsRead {
    markAllNotificationsRead
  }
`;

const CREATE_NOTIFICATION_MUTATION = gql`
  mutation CreateNotification($input: CreateNotificationInput!) {
    createNotification(input: $input)
  }
`;

// Types
interface NotificationPayload {
  id: string;
  globalId: string;
  recipientId: string;
  actorId: string | null;
  notificationType: string;
  targetType: string;
  targetId: string;
  title: string;
  body: string | null;
  actionUrl: string | null;
  status: string;
  readAt: string | null;
  urgency: string;
  payload: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface MarkAllReadPayload {
  success: boolean;
  updated: number;
}

/**
 * Hook for marking a single notification as read
 */
export function useMarkNotificationReadGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { markNotificationRead: NotificationPayload },
    { input: { id: string } }
  >(MARK_NOTIFICATION_READ_MUTATION, {
    refetchQueries: ['Notifications', 'UnreadNotificationCount'],
    onCompleted: (data) => {
      logger.info('Notification', 'Notification marked as read via GraphQL', {
        notificationId: data.markNotificationRead.id,
      });
    },
    onError: (err) => {
      logger.error('Notification', 'Failed to mark notification as read', err);
    },
  });

  const markRead = async (notificationId: string) => {
    const result = await mutate({
      variables: { input: { id: notificationId } },
    });
    return result.data?.markNotificationRead;
  };

  return { markRead, loading, error };
}

/**
 * Hook for marking all notifications as read
 */
export function useMarkAllNotificationsReadGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { markAllNotificationsRead: MarkAllReadPayload }
  >(MARK_ALL_NOTIFICATIONS_READ_MUTATION, {
    refetchQueries: ['Notifications', 'UnreadNotificationCount'],
    onCompleted: (data) => {
      logger.info('Notification', 'All notifications marked as read via GraphQL', {
        updated: data.markAllNotificationsRead.updated,
      });
    },
    onError: (err) => {
      logger.error('Notification', 'Failed to mark all notifications as read', err);
    },
  });

  const markAllRead = async () => {
    const result = await mutate();
    return result.data?.markAllNotificationsRead;
  };

  return { markAllRead, loading, error };
}

/**
 * Input for creating a notification
 */
export interface CreateNotificationInput {
  recipientId: string;
  actorId?: string;
  notificationType: string;
  targetType: string;
  targetId: string;
  title: string;
  body?: string;
  actionUrl?: string;
  urgency?: 'low' | 'medium' | 'high';
  payload?: Record<string, unknown>;
}

/**
 * Hook for creating a notification (admin/dev use)
 */
export function useCreateNotificationGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { createNotification: NotificationPayload },
    { input: CreateNotificationInput }
  >(CREATE_NOTIFICATION_MUTATION, {
    refetchQueries: ['Notifications', 'UnreadNotificationCount'],
    onCompleted: (data) => {
      logger.info('Notification', 'Notification created via GraphQL', {
        notificationId: data.createNotification.globalId,
      });
    },
    onError: (err) => {
      logger.error('Notification', 'Failed to create notification', err);
    },
  });

  const createNotification = async (input: CreateNotificationInput) => {
    const result = await mutate({
      variables: { input },
    });
    return result.data?.createNotification;
  };

  return { createNotification, loading, error };
}
